import { produce } from "immer";

import { Cart, CartItem, CartItemCategory } from "./CartContext";

/**
 * Adds a cart item to the specified cart group.
 * @param cart The current cart state.
 * @param item The cart item to add.
 * @param itemType The type of cart group to add the item to.
 * @returns The updated cart state.
 */
export function addCartItem(
  cart: Cart,
  item: CartItem,
  itemType: CartItemCategory
): Cart {
  return produce(cart, (draftCart) => {
    if (item.quantity <= 0) {
      return;
    }

    const draftGroup = draftCart[itemType];
    const existingItem = draftGroup.find(({ id }) => id === item.id);

    if (existingItem) {
      existingItem.quantity += item.quantity;
    } else {
      draftGroup.push({
        id: item.id,
        quantity: item.quantity,
        price: item.price,
      });
    }
  });
}

/**
 * Removes a cart item from the cart state.
 * @param cart The current cart state.
 * @param itemId The ID of the item to remove.
 * @returns The updated cart state.
 */
export function removeCartItem(cart: Cart, itemId: number): Cart {
  return {
    products: cart.products.filter(({ id }) => id !== itemId),
    dispensers: cart.dispensers.filter(({ id }) => id !== itemId),
  };
}

/**
 * Updates the quantity of an existing cart item.
 * @param cart The current cart state.
 * @param item The cart item to update.
 * @param itemType The type of cart group to update the item in.
 * @returns The updated cart state.
 */
export function updateCartItemQuantity(
  cart: Cart,
  item: CartItem,
  itemType: CartItemCategory
): Cart {
  return produce(cart, (draftCart) => {
    const draftGroup = draftCart[itemType];
    const existingItem = draftGroup.find(({ id }) => id === item.id);

    if (existingItem) {
      existingItem.quantity = item.quantity;
    }

    draftCart[itemType] = draftGroup.filter(({ quantity }) => quantity > 0);
  });
}

/**
 * Compares two cart states to determine if they are equal. Ignores the order of items.
 * @param cartA The first cart state.
 * @param cartB The second cart state.
 * @returns Whether the cart states are equal.
 * @example
 * const cartA = { products: [{ id: 1, quantity: 1, price: 10 }] };
 * const cartB = { products: [{ id: 1, quantity: 1, price: 10 }] };
 * const isEqual = cartsAreEqual(cartA, cartB); // true
 * @example
 * const cartA = { products: [{ id: 1, quantity: 1, price: 10 }, { id: 2, quantity: 5, price: 10 }] };
 * const cartB = { products: [{ id: 2, quantity: 5, price: 10 }, { id: 1, quantity: 1, price: 10 }] };
 * const isEqual = cartsAreEqual(cartA, cartB); // true
 */

export function cartsAreEqual(cartA: Cart, cartB: Cart): boolean {
  if (cartA.products.length !== cartB.products.length) {
    return false;
  }

  if (cartA.dispensers.length !== cartB.dispensers.length) {
    return false;
  }

  return (
    cartA.products.every(existsIn(cartB.products)) &&
    cartA.dispensers.every(existsIn(cartB.dispensers))
  );
}

function existsIn(arr: CartItem[]) {
  return (item: CartItem) =>
    arr.some(({ id, quantity, price }) => {
      return (
        id === item.id && quantity === item.quantity && price === item.price
      );
    });
}
